/**
 * Založenie novej partie: rozostavenie figúrok, stavba a zamiešanie balíčkov,
 * úvodné ruky. Všetka náhoda ide cez seed → partia sa dá presne zreprodukovať.
 */

import type { GameEvent } from './events.js';
import type { Ctx } from './ops.js';
import { freeSpacesInZonesOf } from './movement.js';
import { HERO_SPECIALS } from './effects/index.js';
import { RuleError, askChoice, drawCard, emit } from './ops.js';
import { createSeed, shuffle } from './rng.js';
import type {
  GameMap,
  CardId,
  CardInstance,
  ContentIndex,
  Fighter,
  GameFormat,
  GameState,
  HeroDef,
  PlayerIndex,
  PlayerState,
} from './types.js';

export const STATE_VERSION = 3; // 3: efekty kariet — fronta rozhodnutí a snímka pozícií
export const STARTING_HAND = 5;
export const HAND_LIMIT = 7;
export const ACTIONS_PER_TURN = 2;

export interface NewGameOptions {
  mapId: string;
  players: { heroId: string }[];
  /** Neuvedený formát sa odvodí z počtu hráčov (2 = duel, 3–4 = ffa). */
  format?: GameFormat;
  seed?: number;
  sandbox?: boolean;
  startingPlayer?: PlayerIndex;
  /**
   * Hráči si pred prvou akciou sami rozostavia sidekickov v zóne hrdinu.
   * Server to zapína; bez toho ich rozostaví engine (testy, replay starších zápasov).
   */
  placeSidekicks?: boolean;
}

/**
 * Poradie slotov je zároveň poradím ťahov. V 2v2 sa sloty rozdávajú striedavo
 * (A1, B1, A2, B2), takže `slot % 2` je číslo tímu a striedanie tímov vyjde samo.
 * (Core Rules, „Team play", body 2 a 3)
 */
function teamOfSlot(format: GameFormat, slot: number): number {
  return format === 'teams' ? slot % 2 : slot;
}

export function createGame(
  options: NewGameOptions,
  content: ContentIndex,
): { state: GameState; events: GameEvent[] } {
  const map = content.maps[options.mapId];
  if (!map) throw new RuleError(`Neznáma mapa: ${options.mapId}`);

  const count = options.players.length;
  if (count < 2 || count > 4) throw new RuleError('Hra je pre 2 až 4 hráčov');

  const format: GameFormat = options.format ?? (count === 2 ? 'duel' : 'ffa');
  if (format === 'teams' && count !== 4) throw new RuleError('Tímová hra je pre 4 hráčov');
  if (format === 'duel' && count !== 2) throw new RuleError('Duel je pre dvoch hráčov');

  const starts = map.spaces.filter((s) => s.start !== undefined).length;
  if (starts < count) {
    throw new RuleError(`Mapa ${map.id} má ${starts} štartových priestorov, treba ${count}`);
  }

  const state: GameState = {
    version: STATE_VERSION,
    format,
    seed: options.seed ?? createSeed(),
    rngCursor: 0,
    mapId: options.mapId,
    sandbox: options.sandbox ?? false,
    fighters: [],
    cards: {},
    players: [],
    turn: { player: options.startingPlayer ?? 0, actionsUsed: [], number: 1, startSpaces: {} },
    maneuver: null,
    pending: [],
    combat: null,
    phase: 'setup',
    winner: null,
    seq: 0,
  };

  const ctx: Ctx = { state, content, events: [] };
  const occupied = new Set<string>();

  options.players.forEach((seat, index) => {
    const hero = content.heroes[seat.heroId];
    if (!hero) throw new RuleError(`Neznámy hrdina: ${seat.heroId}`);

    // Štartové priestory sú na doske označené číslom (diamant 1, 2, …).
    const startSpace = map.spaces.find((s) => s.start === index + 1)?.id;
    if (!startSpace) throw new RuleError(`Mapa ${map.id} nemá štartový priestor ${index + 1}`);
    occupied.add(startSpace);

    // Sidekicky idú do samostatných voľných priestorov v tej istej zóne ako hrdina.
    const nearby = freeSpacesInZonesOf(map, occupied, startSpace);
    const fallback = map.spaces.filter((s) => !occupied.has(s.id)).map((s) => s.id);

    const fighters = createFighters(hero, index, startSpace, () => {
      const spaceId = nearby.shift() ?? fallback.find((id) => !occupied.has(id));
      if (!spaceId) throw new RuleError(`Na mape ${map.id} nie je miesto pre sidekicka`);
      occupied.add(spaceId);
      return spaceId;
    });

    state.fighters.push(...fighters);
    state.players.push(createPlayerState(ctx, hero, index, teamOfSlot(format, index)));
  });

  state.phase = 'turn';
  // Snímka pozícií pre karty typu „ak tvoja figúrka začala ťah inde".
  state.turn.startSpaces = Object.fromEntries(state.fighters.map((f) => [f.id, f.spaceId]));
  state.turn.startTokens = {};
  emit(ctx, { type: 'gameStarted', mapId: map.id, startingPlayer: state.turn.player });

  for (const player of state.players) {
    for (let i = 0; i < STARTING_HAND; i++) drawCard(ctx, player.index);
  }

  emit(ctx, { type: 'turnStarted', player: state.turn.player, turnNumber: 1 });

  // Rozostavenie podľa schopností (hmla Invisible Mana) — hráč ju kladie sám,
  // preto sú to rozhodnutia vo fronte, ešte pred prvou akciou prvého ťahu.
  for (const player of state.players) {
    HERO_SPECIALS[player.heroId]?.setup?.(ctx, player.index);
  }
  if (options.placeSidekicks) askSidekickPlacement(ctx, map);
  // „At the start of your turn" platí aj pre úplne prvý ťah hry.
  const first = state.players[state.turn.player];
  if (first) HERO_SPECIALS[first.heroId]?.startOfTurn?.(ctx, first.index);
  return { state, events: ctx.events };
}

/**
 * Rozostavenie sidekickov: každý hráč, počnúc tým, kto začína, postaví svojich
 * sidekickov na priestory v zóne svojho hrdinu. (Core Rules, „Setup")
 * Engine ich predbežne rozostaví sám — hráč smie každého premiestniť, alebo
 * ho nechať, kde stojí (voľba je voliteľná).
 */
function askSidekickPlacement(ctx: Ctx, map: GameMap): void {
  const { state } = ctx;
  const count = state.players.length;
  for (let step = 0; step < count; step++) {
    const index = (state.turn.player + step) % count;
    const hero = state.fighters.find((f) => f.owner === index && f.kind === 'hero');
    if (!hero) continue;
    const zone = freeSpacesInZonesOf(map, new Set([hero.spaceId]), hero.spaceId);
    const sidekicks = state.fighters.filter((f) => f.owner === index && f.kind === 'sidekick');
    for (const sidekick of sidekicks) {
      // Rovnaké mená (tri Harpie) treba rozlíšiť poradím.
      const same = sidekicks.filter((f) => f.name === sidekick.name);
      const which = same.length > 1 ? ` ${same.indexOf(sidekick) + 1}/${same.length}` : '';
      askChoice(ctx, {
        kind: 'place',
        player: index,
        source: null,
        label: `Rozostavenie: kam postaviť ${sidekick.name}${which}? (v zóne hrdinu, alebo nechaj, kde stojí)`,
        optional: true,
        fighterId: sidekick.id,
        spaces: zone,
        swapOwn: true,
      });
    }
  }
}

function createFighters(
  hero: HeroDef,
  owner: PlayerIndex,
  spaceId: string,
  nextSidekickSpace: () => string,
): Fighter[] {
  const fighters: Fighter[] = [
    {
      id: `p${owner}-hero`,
      owner,
      kind: 'hero',
      defId: hero.id,
      name: hero.name,
      hp: hero.hp,
      maxHp: hero.hp,
      move: hero.move,
      attack: hero.attack,
      spaceId,
    },
  ];

  for (const sidekick of hero.sidekicks ?? []) {
    for (let i = 0; i < sidekick.count; i++) {
      fighters.push({
        id: `p${owner}-${sidekick.id}-${i + 1}`,
        owner,
        kind: 'sidekick',
        defId: sidekick.id,
        name: sidekick.name,
        hp: sidekick.hp,
        maxHp: sidekick.hp,
        move: sidekick.move,
        attack: sidekick.attack,
        spaceId: nextSidekickSpace(),
      });
    }
  }
  return fighters;
}

function createPlayerState(
  ctx: Ctx,
  hero: HeroDef,
  index: PlayerIndex,
  team: number,
): PlayerState {
  const deckDef = ctx.content.decks[hero.deckId];
  if (!deckDef) throw new RuleError(`Neznámy balíček: ${hero.deckId}`);

  const ids: CardId[] = [];
  for (const card of deckDef.cards) {
    for (let copy = 1; copy <= card.qty; copy++) {
      const instance: CardInstance = { id: `p${index}-${card.id}-${copy}`, defId: card.id, owner: index };
      ctx.state.cards[instance.id] = instance;
      ids.push(instance.id);
    }
  }

  return {
    index,
    heroId: hero.id,
    team,
    eliminated: false,
    deck: shuffle(ctx.state, ids),
    hand: [],
    discard: [],
  };
}
